import { computed, markRaw, reactive, ref, type Component } from "vue";
import { createSharedComposable } from "@vueuse/core";
import { useFeatures } from "@editor/store/features.store";
import SceneShapeSvg from "@editor/items/sceneShape/SceneShapeSvg.vue";

type NetToolType = "creating" | "selecting" | "dragging" | "special";
export type NetToolKey = "pointer" | "pan" | "target" | "resource" | "index" | "medium" | "text" | "shape" | "radius" | "shapeedit" | "textedit" | "chart";

export const netTools: { [key in NetToolKey]: { id: key, type: NetToolType; hotkey: string; placeholderComponent?: Component } } = {
  pointer: { id: "pointer", type: "selecting", hotkey: "v" },
  pan: { id: "pan", type: "dragging", hotkey: "h" },
  target: { id: "target", type: "creating", hotkey: "e" },
  resource: { id: "resource", type: "creating", hotkey: "r" },
  index: { id: "index", type: "creating", hotkey: "w" },
  medium: { id: "medium", type: "creating", hotkey: "g" },
  text: { id: "text", type: "creating", hotkey: "t" },
  shape: { id: "shape", type: "creating", hotkey: "s", placeholderComponent: markRaw(SceneShapeSvg) },
  radius: { id: "radius", type: "special", hotkey: "" },
  shapeedit: { id: "shapeedit", type: "special", hotkey: "" },
  textedit: { id: "textedit", type: "special", hotkey: "" },
  chart: { id: "chart", type: "creating", hotkey: "c" },
};
export type NetTool = typeof netTools[NetToolKey];

function useNetToolsNew() {
  const toolKey = ref<NetToolKey>("pointer");
  const features = useFeatures();

  const tool = computed(() => {
    return netTools[toolKey.value];
  });

  const availableTools = computed(() => {
    const tools: Record<string, NetTool> = {
      ...netTools
    };
    if (!features.target) delete tools.target;
    if (!features.resource) delete tools.resource;
    if (!features.index) delete tools.index;
    if (!features.design) {
      delete tools.medium;
      delete tools.shape;
      delete tools.text;
    }
    if (!features.netChart) delete tools.chart;
    return tools;
  });

  function setTool(key: NetToolKey) {
    if (availableTools.value[key]) {
      toolKey.value = key;
    }
  }

  function selectByHotkey(key: string) {
    for (const tool of Object.values(availableTools.value)) {
      if (tool.hotkey === key) {
        setTool(tool.id);
        return;
      }
    }
  }

  return reactive({
    tool,
    availableTools,
    setTool,
    selectByHotkey
  });
}
export const useNetTools = createSharedComposable(useNetToolsNew);
